import { Box, Button, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState, store } from "../../../../../redux";
import { MembershipsManagement } from "./MembershipsManagement";
import AddMembership from "./AddMembership";
import { useState } from "react";
import CsvBulkUpload from "./CsvBulkUpload";
import { useTranslation } from "react-i18next";
import ConsentLegalGuardiansConfirmationDialog from "../../../../layout/dialog/ConsentLegalGuardiansConfirmationDialog";
import CustomCheckboxWithoutFormik from "../../../../forms/CustomCheckboxWithoutFormik";

/**
 * @interface MembershipTabProps
 * Defines the props for the MembershipTab component.
 */
interface MembershipTabProps {
  organizationId: string;
  fetchMembershipsData: () => void;
}

/**
 * The `MembershipTab` component is responsible for displaying and managing memberships
 * within an organization. It fetches and displays existing memberships
 * while also allowing users to add new memberships.
 *
 * @param {MembershipTabProps} props - The component props.
 * @returns {JSX.Element} The rendered MembershipTab component.
 */
const MembershipTab = ({ organizationId, fetchMembershipsData }: MembershipTabProps) => {
  const { t } = useTranslation(["common", "organization"]);

  const { memberships, organizationSettings } = useSelector((state: AppState) => ({
    memberships: state.memberships,
    organizationSettings: state.organizationSettings,
  }));

  const isOrganizationUserWithoutMailEnabled =
    store.getState().featureFlagging.featureFlags?.organization_user_without_mail_enabled || false; // TODO ML-2650

  const [isSingleAddSectionOpen, setIsSingleAddSectionOpen] = useState(false);
  const [isMultipleAddSectionOpen, setIsMultipleAddSectionOpen] = useState(false);
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [addUserWithoutMail, setAddUserWithoutMail] = useState(false);

  const maxUserLimit = organizationSettings.organizationSettings.max_user_limit || 0;
  const totalMembers = memberships.memberships.length;
  const hasSlotAvailable = !maxUserLimit || totalMembers < maxUserLimit;

  const skipGuardians =
    organizationSettings.organizationSettings.skip_guardians || false;

  const handleConsentConfirm = () => {
    setIsConsentDialogOpen(false);
    setIsMultipleAddSectionOpen(!isMultipleAddSectionOpen);
    if (isSingleAddSectionOpen) setIsSingleAddSectionOpen(false);
  };

  const handleConsentCancel = () => {
    setIsConsentDialogOpen(false);
  };

  const handleToggleSingle = () => {
    setIsSingleAddSectionOpen(!isSingleAddSectionOpen);
    if (isMultipleAddSectionOpen) setIsMultipleAddSectionOpen(false);
    setAddUserWithoutMail(false);
  };

  const handleToggleMultiple = () => {
    if (!isMultipleAddSectionOpen) {
      if (!skipGuardians) {
        setIsConsentDialogOpen(true);
      } else {
        setIsMultipleAddSectionOpen(true);
      }
    } else {
      setIsMultipleAddSectionOpen(false);
      setAddUserWithoutMail(false);
    }
  };

  const usersWithoutMailHint = (
    <Box>
      <Typography variant="body1" style={{ marginBottom: "0.5rem" }}>
        {t("membership.noMailInfo.infoUsersWithoutMail1", { ns: "organization" })}
      </Typography>
      <Typography variant="body1">
        {t("membership.noMailInfo.infoUsersWithoutMail2", { ns: "organization" })}
      </Typography>
      <Box mt={2}>
        <CustomCheckboxWithoutFormik
          checked={addUserWithoutMail}
          onChange={(_, checked) => setAddUserWithoutMail(checked)}
          label={t("membership.noMailInfo.checkboxLabel", { ns: "organization" })}
          tooltipText={t("membership.noMailInfo.checkboxTooltip", {
            ns: "organization",
          })}
        />
      </Box>
    </Box>
  );

  const addButtons = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={3}
      flexDirection={{ xs: "column", sm: "row" }}
    >
      <Box>
        {maxUserLimit > 0 ? (
          <Typography variant="body1">
            {t("membership.maxUserLimit", { ns: "organization", maxUserLimit })}
          </Typography>
        ) : (
          <Typography variant="body1">
            {t("membership.unlimitedUsers", { ns: "organization" })}
          </Typography>
        )}

        {!hasSlotAvailable && (
          <Typography variant="body1" color="error">
            {t("membership.noSlotsAvailable", { ns: "organization" })}
          </Typography>
        )}
      </Box>

      {hasSlotAvailable ? (
        <Box
          flexDirection={{ xs: "column", sm: "row" }}
          style={{
            marginTop: "1.25rem",
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
          }}
        >
          {!isMultipleAddSectionOpen && (
            <Button
              onClick={handleToggleSingle}
              variant={isSingleAddSectionOpen ? "outlined" : "contained"}
              color="primary"
            >
              {isSingleAddSectionOpen
                ? t("forms.buttons.close", { ns: "common" })
                : t("membership.addMember", { ns: "organization" })}
            </Button>
          )}

          {!isSingleAddSectionOpen && (
            <Button
              onClick={handleToggleMultiple}
              variant={isMultipleAddSectionOpen ? "outlined" : "contained"}
              color="primary"
            >
              {isMultipleAddSectionOpen
                ? t("forms.buttons.close", { ns: "common" })
                : t("membership.addMembers", { ns: "organization" })}
            </Button>
          )}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box>
      <ConsentLegalGuardiansConfirmationDialog
        open={isConsentDialogOpen}
        onClose={handleConsentCancel}
        onConfirm={handleConsentConfirm}
        content={t("skipGuardian", { ns: "organization" })}
      />
      {addButtons}

      {(isSingleAddSectionOpen || isMultipleAddSectionOpen) && isOrganizationUserWithoutMailEnabled ? (
        <Box mt={2}>{usersWithoutMailHint}</Box>
      ) : null}

      {hasSlotAvailable && isSingleAddSectionOpen ? (
        <AddMembership
          organizationId={organizationId}
          onMembershipAdded={fetchMembershipsData}
          setIsOpenSection={setIsSingleAddSectionOpen}
          skipGuardians={skipGuardians}
          addUserWithoutMail={addUserWithoutMail}
        />
      ) : null}
      {hasSlotAvailable && isMultipleAddSectionOpen ? (
        <Box style={{ marginTop: "3rem" }}>
          <CsvBulkUpload
            organizationId={organizationId}
            addUserWithoutMail={addUserWithoutMail}
          />
        </Box>
      ) : null}
      {!isSingleAddSectionOpen && !isMultipleAddSectionOpen && (
        <MembershipsManagement
          organizationId={organizationId}
          memberships={memberships}
        />
      )}
    </Box>
  );
};

export default MembershipTab;
